import React, { useState, useEffect } from 'react';

const ValidationResults = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  useEffect(() => {
    fetch('/validation_results.json')
      .then(res => res.json())
      .then(data => {
        setResults(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading results:', err);
        setLoading(false);
      });
  }, []);

  const getAccuracyColor = (error) => {
    if (error <= 2) return 'text-green-600 bg-green-50';
    if (error <= 5) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getDivorceColor = (risk) => {
    if (risk === 'VERY_HIGH') return 'bg-red-100 text-red-800';
    if (risk === 'HIGH') return 'bg-orange-100 text-orange-800';
    if (risk === 'MODERATE') return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  const filteredResults = results.filter(r => {
    if (filter === 'accurate') return r.status === 'success' && r.accuracy?.age_error <= 3;
    if (filter === 'needs-review') return r.status === 'success' && r.accuracy?.age_error > 5;
    return true;
  });

  const sortedResults = [...filteredResults].sort((a, b) => {
    if (sortBy === 'accuracy') {
      return (a.accuracy?.age_error || 99) - (b.accuracy?.age_error || 99);
    }
    if (sortBy === 'marriages') {
      return (b.actual?.marriages || 0) - (a.actual?.marriages || 0);
    }
    return a.name.localeCompare(b.name);
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading validation results...</p>
        </div>
      </div>
    );
  }

  const successCount = results.filter(r => r.status === 'success').length;
  const avgError = results
    .filter(r => r.status === 'success' && r.accuracy)
    .reduce((sum, r) => sum + r.accuracy.age_error, 0) / (successCount || 1);
  const accurateCount = results.filter(r => r.status === 'success' && r.accuracy?.age_error <= 3).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            🎯 Algorithm Validation Results
          </h1>
          <p className="text-lg text-gray-600">
            V114 Ultimate Engine tested on {results.length} historical figures
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-purple-500">
            <p className="text-sm text-gray-600 mb-1">Total Analyzed</p>
            <p className="text-3xl font-bold text-gray-900">{results.length}</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500">
            <p className="text-sm text-gray-600 mb-1">Successful</p>
            <p className="text-3xl font-bold text-green-600">{successCount}</p>
            <p className="text-xs text-gray-500">{((successCount/results.length)*100).toFixed(1)}%</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500">
            <p className="text-sm text-gray-600 mb-1">±3 Years Accuracy</p>
            <p className="text-3xl font-bold text-blue-600">{accurateCount}</p>
            <p className="text-xs text-gray-500">{((accurateCount/(successCount || 1))*100).toFixed(1)}%</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-orange-500">
            <p className="text-sm text-gray-600 mb-1">Avg Age Error</p>
            <p className="text-3xl font-bold text-orange-600">{avgError.toFixed(1)}</p>
            <p className="text-xs text-gray-500">years</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex gap-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  filter === 'all'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All ({results.length})
              </button>
              <button
                onClick={() => setFilter('accurate')}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  filter === 'accurate'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Accurate ({accurateCount})
              </button>
              <button
                onClick={() => setFilter('needs-review')}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  filter === 'needs-review'
                    ? 'bg-orange-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Needs Review
              </button>
            </div>

            <div className="flex gap-2 items-center">
              <span className="text-sm text-gray-600">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="name">Name</option>
                <option value="accuracy">Accuracy</option>
                <option value="marriages">Marriages</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Person</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold">Predicted Age</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold">Actual Age</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold">Error</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold">Divorce Risk</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold">Marriages</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold">Confidence</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedResults.map((result, index) => (
                  <tr 
                    key={index}
                    className={`hover:bg-gray-50 transition ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                    }`}
                  >
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-semibold text-gray-900">{result.name}</p>
                        {result.status !== 'success' && (
                          <p className="text-xs text-red-600">{result.status}: {result.reason || result.error}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {result.predicted ? (
                        <span className="font-semibold text-purple-700">
                          {result.predicted.marriage_age}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="font-semibold text-gray-900">
                        {result.actual?.first_age || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {result.accuracy ? (
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                          getAccuracyColor(result.accuracy.age_error)
                        }`}>
                          {result.accuracy.age_error === 0 ? '✓ Perfect' : `±${result.accuracy.age_error} yrs`}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {result.predicted ? (
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          getDivorceColor(result.predicted.divorce_risk)
                        }`}>
                          {result.predicted.divorce_risk}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-purple-700 font-semibold">
                          {result.predicted?.total_marriages || '-'}
                        </span>
                        <span className="text-gray-400">/</span>
                        <span className="text-gray-900 font-semibold">
                          {result.actual?.marriages || '-'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {result.predicted ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-gradient-to-r from-purple-600 to-blue-600 h-2 rounded-full"
                              style={{ width: `${result.predicted.confidence}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-700">
                            {result.predicted.confidence}%
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-8 text-center text-sm text-gray-600">
          <p>
            Algorithm: V114 Ultimate Engine | Method: Double Transit + 5-Layer Synchronization
          </p>
          <p className="mt-1">
            Based on Classical Vedic Astrology + DOMAIN Documents + Web Research Verified
          </p>
        </div>
      </div>
    </div>
  );
};

export default ValidationResults;
